import { Course } from '../courses/types';

export const DESKTOP_APPLICATION_DEVELOPMENT: Course = {
  id: 'desktop-app-development',
  title: 'Desktop Application Development',
  description: 'Build cross-platform desktop applications using modern frameworks and technologies.',
  level: 'intermediate',
  duration: '6-8 weeks',
  estimatedHours: 35,
  tags: ['Electron', 'C#', 'WPF', 'JavaFX', 'Cross-Platform', 'Desktop'],
  prerequisites: ['Basic programming knowledge', 'Understanding of UI/UX principles', 'HTML/CSS/JavaScript basics'],
  learningOutcomes: [
    'Build cross-platform desktop apps with Electron',
    'Create native Windows applications with C# and WPF',
    'Develop Java desktop applications with JavaFX',
    'Implement file system operations and OS integration',
    'Design responsive desktop user interfaces',
    'Package and distribute desktop applications'
  ],
  modules: [
    {
      id: 'desktop-fundamentals',
      title: 'Desktop Development Fundamentals',
      description: 'Understand desktop application architecture and platform considerations.',
      duration: '2 hours',
      difficulty: 'beginner',
      content: `
# Desktop Development Fundamentals

## Desktop vs Web Applications

### Key Differences
- **Performance**: Direct system access and native APIs
- **User Experience**: Platform-specific UI conventions
- **Security**: Different security models and considerations
- **Distribution**: Installation packages vs web deployment
- **Offline Capability**: Full functionality without internet

### Platform Considerations
- **Windows**: .NET Framework, WPF, UWP, Win32 APIs
- **macOS**: Cocoa, AppKit, Mac App Store guidelines
- **Linux**: GTK, Qt, various distributions
- **Cross-Platform**: Electron, Qt, Flutter Desktop

## Application Architecture Patterns

### Model-View-ViewModel (MVVM)
\`\`\`csharp
// Example MVVM structure in C#
public class MainViewModel : INotifyPropertyChanged
{
    private string _title;
    private ObservableCollection<Item> _items;
    
    public string Title
    {
        get => _title;
        set
        {
            _title = value;
            OnPropertyChanged();
        }
    }
    
    public ObservableCollection<Item> Items
    {
        get => _items;
        set
        {
            _items = value;
            OnPropertyChanged();
        }
    }
    
    public ICommand AddItemCommand { get; }
    public ICommand DeleteItemCommand { get; }
    
    public MainViewModel()
    {
        Items = new ObservableCollection<Item>();
        AddItemCommand = new RelayCommand(AddItem);
        DeleteItemCommand = new RelayCommand<Item>(DeleteItem);
    }
    
    private void AddItem()
    {
        Items.Add(new Item { Name = "New Item", Created = DateTime.Now });
    }
    
    private void DeleteItem(Item item)
    {
        Items.Remove(item);
    }
    
    public event PropertyChangedEventHandler PropertyChanged;
    
    protected virtual void OnPropertyChanged([CallerMemberName] string propertyName = null)
    {
        PropertyChanged?.Invoke(this, new PropertyChangedEventArgs(propertyName));
    }
}
\`\`\`

### Model-View-Controller (MVC)
\`\`\`java
// Example MVC structure in JavaFX
public class ItemController {
    private ItemModel model;
    private ItemView view;
    
    public ItemController(ItemModel model, ItemView view) {
        this.model = model;
        this.view = view;
        
        initController();
    }
    
    private void initController() {
        view.getAddButton().setOnAction(e -> addItem());
        view.getDeleteButton().setOnAction(e -> deleteSelectedItem());
        view.getItemList().getSelectionModel().selectedItemProperty()
            .addListener((obs, oldSelection, newSelection) -> {
                view.getDeleteButton().setDisable(newSelection == null);
            });
    }
    
    private void addItem() {
        String itemName = view.getItemNameField().getText();
        if (!itemName.isEmpty()) {
            model.addItem(new Item(itemName));
            view.getItemNameField().clear();
            updateView();
        }
    }
    
    private void deleteSelectedItem() {
        Item selected = view.getItemList().getSelectionModel().getSelectedItem();
        if (selected != null) {
            model.removeItem(selected);
            updateView();
        }
    }
    
    private void updateView() {
        view.getItemList().setItems(FXCollections.observableArrayList(model.getItems()));
    }
}
\`\`\`

## UI Design Principles

### Platform-Native Design
- **Windows**: Fluent Design System, Windows UI guidelines
- **macOS**: Human Interface Guidelines, native controls
- **Linux**: GTK/Qt design principles

### Responsive Desktop Layout
\`\`\`xml
<!-- WPF responsive layout example -->
<Grid>
    <Grid.RowDefinitions>
        <RowDefinition Height="Auto"/>
        <RowDefinition Height="*"/>
        <RowDefinition Height="Auto"/>
    </Grid.RowDefinitions>
    
    <Grid.ColumnDefinitions>
        <ColumnDefinition Width="250" MinWidth="200"/>
        <ColumnDefinition Width="*" MinWidth="300"/>
    </Grid.ColumnDefinitions>
    
    <!-- Header -->
    <Border Grid.Row="0" Grid.ColumnSpan="2" Background="DarkBlue" Padding="10">
        <TextBlock Text="Application Title" Foreground="White" FontSize="18"/>
    </Border>
    
    <!-- Sidebar -->
    <TreeView Grid.Row="1" Grid.Column="0" x:Name="NavigationTree"/>
    
    <!-- Content Area -->
    <ContentControl Grid.Row="1" Grid.Column="1" x:Name="MainContent" Margin="10"/>
    
    <!-- Status Bar -->
    <StatusBar Grid.Row="2" Grid.ColumnSpan="2">
        <StatusBarItem Content="Ready"/>
        <StatusBarItem HorizontalAlignment="Right" Content="{Binding StatusText}"/>
    </StatusBar>
    
    <!-- Splitter -->
    <GridSplitter Grid.Row="1" Grid.Column="0" 
                  HorizontalAlignment="Right" 
                  VerticalAlignment="Stretch" 
                  Width="5"/>
</Grid>
\`\`\`

## File System Operations

### Cross-Platform File Handling
\`\`\`javascript
// Electron file operations
const { dialog, app } = require('electron');
const fs = require('fs');
const path = require('path');

class FileManager {
    constructor() {
        this.currentFile = null;
    }
    
    async openFile() {
        const result = await dialog.showOpenDialog({
            title: 'Open File',
            filters: [
                { name: 'Text Files', extensions: ['txt', 'md'] },
                { name: 'All Files', extensions: ['*'] }
            ],
            properties: ['openFile']
        });
        
        if (!result.canceled && result.filePaths.length > 0) {
            this.currentFile = result.filePaths[0];
            return this.readFile(this.currentFile);
        }
        
        return null;
    }
    
    async saveFile(content, filePath = null) {
        let targetPath = filePath || this.currentFile;
        
        if (!targetPath) {
            const result = await dialog.showSaveDialog({
                title: 'Save File',
                filters: [
                    { name: 'Text Files', extensions: ['txt'] },
                    { name: 'All Files', extensions: ['*'] }
                ]
            });
            
            if (!result.canceled) {
                targetPath = result.filePath;
                this.currentFile = targetPath;
            } else {
                return false;
            }
        }
        
        try {
            await fs.promises.writeFile(targetPath, content, 'utf8');
            return true;
        } catch (error) {
            console.error('Save failed:', error);
            return false;
        }
    }
    
    async readFile(filePath) {
        try {
            const content = await fs.promises.readFile(filePath, 'utf8');
            return content;
        } catch (error) {
            console.error('Read failed:', error);
            return null;
        }
    }
    
    getRecentFiles() {
        // Retrieve from app settings/registry
        return JSON.parse(localStorage.getItem('recentFiles') || '[]');
    }
    
    addToRecentFiles(filePath) {
        let recent = this.getRecentFiles();
        recent = recent.filter(path => path !== filePath);
        recent.unshift(filePath);
        recent = recent.slice(0, 10); // Keep only 10 recent files
        
        localStorage.setItem('recentFiles', JSON.stringify(recent));
    }
}
\`\`\`

## Application Lifecycle

### Startup and Shutdown
\`\`\`csharp
// WPF Application lifecycle
public partial class App : Application
{
    protected override void OnStartup(StartupEventArgs e)
    {
        base.OnStartup(e);
        
        // Initialize logging
        InitializeLogging();
        
        // Load application settings
        LoadSettings();
        
        // Check for updates
        CheckForUpdates();
        
        // Create and show main window
        var mainWindow = new MainWindow();
        mainWindow.Show();
    }
    
    protected override void OnExit(ExitEventArgs e)
    {
        // Save application settings
        SaveSettings();
        
        // Clean up resources
        CleanupResources();
        
        base.OnExit(e);
    }
    
    private void InitializeLogging()
    {
        // Configure logging framework
        Log.Logger = new LoggerConfiguration()
            .WriteTo.File("logs/app.log", rollingInterval: RollingInterval.Day)
            .WriteTo.Console()
            .CreateLogger();
    }
    
    private void LoadSettings()
    {
        try
        {
            var settings = Settings.Default;
            settings.Reload();
            
            // Apply settings to application
            ApplyTheme(settings.Theme);
            ApplyLanguage(settings.Language);
        }
        catch (Exception ex)
        {
            Log.Error(ex, "Failed to load settings");
        }
    }
    
    private void SaveSettings()
    {
        try
        {
            Settings.Default.Save();
        }
        catch (Exception ex)
        {
            Log.Error(ex, "Failed to save settings");
        }
    }
}
\`\`\`

## Threading and Concurrency

### Background Operations
\`\`\`csharp
// Async operations in desktop apps
public class DataProcessor
{
    private readonly IProgress<int> _progress;
    private CancellationTokenSource _cancellationTokenSource;
    
    public DataProcessor(IProgress<int> progress)
    {
        _progress = progress;
    }
    
    public async Task<ProcessResult> ProcessDataAsync(IEnumerable<DataItem> items)
    {
        _cancellationTokenSource = new CancellationTokenSource();
        var token = _cancellationTokenSource.Token;
        
        return await Task.Run(async () =>
        {
            var results = new List<ProcessedItem>();
            var itemList = items.ToList();
            
            for (int i = 0; i < itemList.Count; i++)
            {
                token.ThrowIfCancellationRequested();
                
                // Simulate processing
                await Task.Delay(100, token);
                
                var processed = ProcessItem(itemList[i]);
                results.Add(processed);
                
                // Report progress
                int progressPercentage = (i + 1) * 100 / itemList.Count;
                _progress?.Report(progressPercentage);
            }
            
            return new ProcessResult { Items = results, Success = true };
        }, token);
    }
    
    public void CancelProcessing()
    {
        _cancellationTokenSource?.Cancel();
    }
    
    private ProcessedItem ProcessItem(DataItem item)
    {
        // Actual processing logic
        return new ProcessedItem
        {
            Id = item.Id,
            ProcessedData = $"Processed: {item.Data}",
            Timestamp = DateTime.Now
        };
    }
}

// Usage in ViewModel
public class MainViewModel
{
    public async void ProcessData()
    {
        var progress = new Progress<int>(percentage =>
        {
            // Update UI on main thread
            Application.Current.Dispatcher.Invoke(() =>
            {
                ProgressValue = percentage;
                StatusText = $"Processing... {percentage}%";
            });
        });
        
        try
        {
            IsProcessing = true;
            var processor = new DataProcessor(progress);
            var result = await processor.ProcessDataAsync(DataItems);
            
            if (result.Success)
            {
                ProcessedItems = new ObservableCollection<ProcessedItem>(result.Items);
                StatusText = "Processing completed successfully";
            }
        }
        catch (OperationCanceledException)
        {
            StatusText = "Processing cancelled";
        }
        catch (Exception ex)
        {
            StatusText = $"Error: {ex.Message}";
        }
        finally
        {
            IsProcessing = false;
            ProgressValue = 0;
        }
    }
}
\`\`\`
      `,
      learningObjectives: [
        'Understand desktop application architecture',
        'Learn platform-specific design principles',
        'Implement file system operations',
        'Handle application lifecycle events'
      ]
    },
    {
      id: 'electron-development',
      title: 'Electron Framework',
      description: 'Build cross-platform desktop apps using web technologies.',
      duration: '3 hours',
      difficulty: 'intermediate',
      content: `
# Electron Framework Development

## Setting Up Electron

### Project Structure
\`\`\`javascript
// package.json
{
  "name": "my-electron-app",
  "version": "1.0.0",
  "description": "A sample Electron application",
  "main": "main.js",
  "scripts": {
    "start": "electron .",
    "build": "electron-builder",
    "dev": "electron . --enable-logging"
  },
  "devDependencies": {
    "electron": "^latest",
    "electron-builder": "^latest"
  },
  "build": {
    "appId": "com.example.myapp",
    "productName": "My Electron App",
    "directories": {
      "output": "dist"
    },
    "files": [
      "**/*",
      "!node_modules",
      "!src"
    ]
  }
}
\`\`\`

### Main Process Setup
\`\`\`javascript
// main.js - Main process
const { app, BrowserWindow, Menu, dialog, ipcMain } = require('electron');
const path = require('path');
const isDev = require('electron-is-dev');

class ElectronApp {
    constructor() {
        this.mainWindow = null;
        this.setupEventHandlers();
    }
    
    setupEventHandlers() {
        app.whenReady().then(() => this.createWindow());
        
        app.on('window-all-closed', () => {
            if (process.platform !== 'darwin') {
                app.quit();
            }
        });
        
        app.on('activate', () => {
            if (BrowserWindow.getAllWindows().length === 0) {
                this.createWindow();
            }
        });
        
        // Handle IPC messages
        this.setupIpcHandlers();
    }
    
    createWindow() {
        this.mainWindow = new BrowserWindow({
            width: 1200,
            height: 800,
            minWidth: 600,
            minHeight: 400,
            webPreferences: {
                nodeIntegration: false,
                contextIsolation: true,
                enableRemoteModule: false,
                preload: path.join(__dirname, 'preload.js')
            },
            icon: path.join(__dirname, 'assets/icon.png'),
            show: false, // Don't show until ready
            titleBarStyle: 'default'
        });
        
        // Load the app
        if (isDev) {
            this.mainWindow.loadURL('http://localhost:3000');
            this.mainWindow.webContents.openDevTools();
        } else {
            this.mainWindow.loadFile('build/index.html');
        }
        
        // Show window when ready to prevent visual flash
        this.mainWindow.once('ready-to-show', () => {
            this.mainWindow.show();
        });
        
        // Create application menu
        this.createMenu();
        
        // Handle window events
        this.mainWindow.on('closed', () => {
            this.mainWindow = null;
        });
    }
    
    createMenu() {
        const template = [
            {
                label: 'File',
                submenu: [
                    {
                        label: 'New',
                        accelerator: 'CmdOrCtrl+N',
                        click: () => this.newFile()
                    },
                    {
                        label: 'Open',
                        accelerator: 'CmdOrCtrl+O',
                        click: () => this.openFile()
                    },
                    {
                        label: 'Save',
                        accelerator: 'CmdOrCtrl+S',
                        click: () => this.saveFile()
                    },
                    { type: 'separator' },
                    {
                        label: 'Exit',
                        accelerator: process.platform === 'darwin' ? 'Cmd+Q' : 'Ctrl+Q',
                        click: () => app.quit()
                    }
                ]
            },
            {
                label: 'Edit',
                submenu: [
                    { role: 'undo' },
                    { role: 'redo' },
                    { type: 'separator' },
                    { role: 'cut' },
                    { role: 'copy' },
                    { role: 'paste' },
                    { role: 'selectall' }
                ]
            },
            {
                label: 'View',
                submenu: [
                    { role: 'reload' },
                    { role: 'forceReload' },
                    { role: 'toggleDevTools' },
                    { type: 'separator' },
                    { role: 'resetZoom' },
                    { role: 'zoomIn' },
                    { role: 'zoomOut' },
                    { type: 'separator' },
                    { role: 'togglefullscreen' }
                ]
            },
            {
                label: 'Help',
                submenu: [
                    {
                        label: 'About',
                        click: () => this.showAbout()
                    }
                ]
            }
        ];
        
        const menu = Menu.buildFromTemplate(template);
        Menu.setApplicationMenu(menu);
    }
    
    async newFile() {
        this.mainWindow.webContents.send('menu-new-file');
    }
    
    async openFile() {
        const result = await dialog.showOpenDialog(this.mainWindow, {
            title: 'Open File',
            filters: [
                { name: 'Text Files', extensions: ['txt', 'md', 'json'] },
                { name: 'All Files', extensions: ['*'] }
            ],
            properties: ['openFile']
        });
        
        if (!result.canceled && result.filePaths.length > 0) {
            this.mainWindow.webContents.send('menu-open-file', result.filePaths[0]);
        }
    }
    
    async saveFile() {
        this.mainWindow.webContents.send('menu-save-file');
    }
    
    showAbout() {
        dialog.showMessageBox(this.mainWindow, {
            type: 'info',
            title: 'About',
            message: 'My Electron App',
            detail: 'Version 1.0.0\\nBuilt with Electron and modern web technologies.'
        });
    }
    
    setupIpcHandlers() {
        // Handle save dialog
        ipcMain.handle('show-save-dialog', async (event, options) => {
            const result = await dialog.showSaveDialog(this.mainWindow, options);
            return result;
        });
        
        // Handle file operations
        ipcMain.handle('read-file', async (event, filePath) => {
            try {
                const fs = require('fs').promises;
                const content = await fs.readFile(filePath, 'utf8');
                return { success: true, content };
            } catch (error) {
                return { success: false, error: error.message };
            }
        });
        
        ipcMain.handle('write-file', async (event, filePath, content) => {
            try {
                const fs = require('fs').promises;
                await fs.writeFile(filePath, content, 'utf8');
                return { success: true };
            } catch (error) {
                return { success: false, error: error.message };
            }
        });
        
        // Handle app info
        ipcMain.handle('get-app-version', () => {
            return app.getVersion();
        });
    }
}

// Create and run the app
const electronApp = new ElectronApp();
\`\`\`

### Preload Script (Security Bridge)
\`\`\`javascript
// preload.js - Secure bridge between main and renderer
const { contextBridge, ipcRenderer } = require('electron');

// Expose protected methods that allow the renderer process to use
// the ipcRenderer without exposing the entire object
contextBridge.exposeInMainWorld('electronAPI', {
    // File operations
    readFile: (filePath) => ipcRenderer.invoke('read-file', filePath),
    writeFile: (filePath, content) => ipcRenderer.invoke('write-file', filePath, content),
    showSaveDialog: (options) => ipcRenderer.invoke('show-save-dialog', options),
    
    // App info
    getAppVersion: () => ipcRenderer.invoke('get-app-version'),
    
    // Menu events (from main to renderer)
    onMenuNewFile: (callback) => ipcRenderer.on('menu-new-file', callback),
    onMenuOpenFile: (callback) => ipcRenderer.on('menu-open-file', callback),
    onMenuSaveFile: (callback) => ipcRenderer.on('menu-save-file', callback),
    
    // Remove listeners
    removeAllListeners: (channel) => ipcRenderer.removeAllListeners(channel)
});

// Platform info
contextBridge.exposeInMainWorld('platform', {
    isWindows: process.platform === 'win32',
    isMac: process.platform === 'darwin',
    isLinux: process.platform === 'linux'
});
\`\`\`

## React Integration

### Electron + React App
\`\`\`jsx
// App.js - Main React component
import React, { useState, useEffect } from 'react';
import FileEditor from './components/FileEditor';
import StatusBar from './components/StatusBar';
import './App.css';

function App() {
    const [currentFile, setCurrentFile] = useState(null);
    const [fileContent, setFileContent] = useState('');
    const [isDirty, setIsDirty] = useState(false);
    const [appVersion, setAppVersion] = useState('');
    
    useEffect(() => {
        // Get app version
        window.electronAPI.getAppVersion().then(setAppVersion);
        
        // Setup menu event listeners
        window.electronAPI.onMenuNewFile(() => {
            handleNewFile();
        });
        
        window.electronAPI.onMenuOpenFile((event, filePath) => {
            handleOpenFile(filePath);
        });
        
        window.electronAPI.onMenuSaveFile(() => {
            handleSaveFile();
        });
        
        // Cleanup listeners on unmount
        return () => {
            window.electronAPI.removeAllListeners('menu-new-file');
            window.electronAPI.removeAllListeners('menu-open-file');
            window.electronAPI.removeAllListeners('menu-save-file');
        };
    }, []);
    
    const handleNewFile = () => {
        if (isDirty) {
            // Show save confirmation dialog
            const shouldSave = window.confirm('Save current file before creating new one?');
            if (shouldSave) {
                handleSaveFile().then(() => {
                    createNewFile();
                });
                return;
            }
        }
        createNewFile();
    };
    
    const createNewFile = () => {
        setCurrentFile(null);
        setFileContent('');
        setIsDirty(false);
    };
    
    const handleOpenFile = async (filePath) => {
        try {
            const result = await window.electronAPI.readFile(filePath);
            if (result.success) {
                setCurrentFile(filePath);
                setFileContent(result.content);
                setIsDirty(false);
            } else {
                alert(\`Failed to open file: \${result.error}\`);
            }
        } catch (error) {
            alert(\`Error opening file: \${error.message}\`);
        }
    };
    
    const handleSaveFile = async () => {
        let filePath = currentFile;
        
        if (!filePath) {
            const result = await window.electronAPI.showSaveDialog({
                title: 'Save File',
                filters: [
                    { name: 'Text Files', extensions: ['txt'] },
                    { name: 'All Files', extensions: ['*'] }
                ]
            });
            
            if (result.canceled) return;
            filePath = result.filePath;
        }
        
        try {
            const result = await window.electronAPI.writeFile(filePath, fileContent);
            if (result.success) {
                setCurrentFile(filePath);
                setIsDirty(false);
            } else {
                alert(\`Failed to save file: \${result.error}\`);
            }
        } catch (error) {
            alert(\`Error saving file: \${error.message}\`);
        }
    };
    
    const handleContentChange = (newContent) => {
        setFileContent(newContent);
        setIsDirty(true);
    };
    
    return (
        <div className="app">
            <div className="app-header">
                <h1>Text Editor</h1>
                {currentFile && (
                    <span className="file-path">{currentFile} {isDirty ? '*' : ''}</span>
                )}
            </div>
            
            <div className="app-content">
                <FileEditor 
                    content={fileContent}
                    onChange={handleContentChange}
                />
            </div>
            
            <StatusBar 
                currentFile={currentFile}
                isDirty={isDirty}
                appVersion={appVersion}
            />
        </div>
    );
}

export default App;
\`\`\`

### File Editor Component
\`\`\`jsx
// components/FileEditor.js
import React, { useRef, useEffect } from 'react';
import './FileEditor.css';

const FileEditor = ({ content, onChange }) => {
    const textareaRef = useRef(null);
    
    useEffect(() => {
        // Focus textarea when component mounts
        if (textareaRef.current) {
            textareaRef.current.focus();
        }
    }, []);
    
    const handleKeyDown = (e) => {
        // Handle tab insertion
        if (e.key === 'Tab') {
            e.preventDefault();
            const textarea = e.target;
            const start = textarea.selectionStart;
            const end = textarea.selectionEnd;
            
            const newContent = content.substring(0, start) + '\\t' + content.substring(end);
            onChange(newContent);
            
            // Reset cursor position
            setTimeout(() => {
                textarea.selectionStart = textarea.selectionEnd = start + 1;
            }, 0);
        }
        
        // Handle Ctrl+S (save shortcut)
        if (e.ctrlKey && e.key === 's') {
            e.preventDefault();
            // Save functionality is handled by menu
        }
    };
    
    return (
        <div className="file-editor">
            <textarea
                ref={textareaRef}
                value={content}
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={handleKeyDown}
                className="editor-textarea"
                placeholder="Start typing..."
                spellCheck={false}
            />
        </div>
    );
};

export default FileEditor;
\`\`\`

## Advanced Electron Features

### Auto-Updater
\`\`\`javascript
// updater.js
const { autoUpdater } = require('electron-updater');
const { dialog } = require('electron');

class AppUpdater {
    constructor(mainWindow) {
        this.mainWindow = mainWindow;
        this.setupAutoUpdater();
    }
    
    setupAutoUpdater() {
        autoUpdater.autoDownload = false;
        autoUpdater.autoInstallOnAppQuit = true;
        
        autoUpdater.on('checking-for-update', () => {
            console.log('Checking for update...');
        });
        
        autoUpdater.on('update-available', (info) => {
            console.log('Update available:', info.version);
            this.showUpdateAvailable(info);
        });
        
        autoUpdater.on('update-not-available', (info) => {
            console.log('Update not available');
        });
        
        autoUpdater.on('error', (err) => {
            console.error('Update error:', err);
        });
        
        autoUpdater.on('download-progress', (progressObj) => {
            this.showDownloadProgress(progressObj);
        });
        
        autoUpdater.on('update-downloaded', (info) => {
            console.log('Update downloaded');
            this.showUpdateReady();
        });
    }
    
    checkForUpdates() {
        autoUpdater.checkForUpdatesAndNotify();
    }
    
    async showUpdateAvailable(info) {
        const result = await dialog.showMessageBox(this.mainWindow, {
            type: 'info',
            title: 'Update Available',
            message: \`A new version (\${info.version}) is available. Would you like to download it now?\`,
            buttons: ['Download', 'Later'],
            defaultId: 0
        });
        
        if (result.response === 0) {
            autoUpdater.downloadUpdate();
        }
    }
    
    showDownloadProgress(progressObj) {
        // Send progress to renderer process
        this.mainWindow.webContents.send('update-download-progress', {
            percent: progressObj.percent,
            transferred: progressObj.transferred,
            total: progressObj.total
        });
    }
    
    async showUpdateReady() {
        const result = await dialog.showMessageBox(this.mainWindow, {
            type: 'info',
            title: 'Update Ready',
            message: 'Update has been downloaded. Restart the application to apply the update.',
            buttons: ['Restart Now', 'Later'],
            defaultId: 0
        });
        
        if (result.response === 0) {
            autoUpdater.quitAndInstall();
        }
    }
}

module.exports = AppUpdater;
\`\`\`

### System Integration
\`\`\`javascript
// systemIntegration.js
const { shell, nativeTheme, systemPreferences } = require('electron');
const os = require('os');

class SystemIntegration {
    constructor() {
        this.setupSystemHandlers();
    }
    
    setupSystemHandlers() {
        // Handle theme changes
        nativeTheme.on('updated', () => {
            this.onThemeChanged();
        });
        
        // Handle system sleep/wake events
        if (process.platform === 'darwin') {
            systemPreferences.subscribeNotification(
                'NSWorkspaceWillSleepNotification',
                () => this.onSystemSleep()
            );
            
            systemPreferences.subscribeNotification(
                'NSWorkspaceDidWakeNotification',
                () => this.onSystemWake()
            );
        }
    }
    
    onThemeChanged() {
        const isDarkMode = nativeTheme.shouldUseDarkColors;
        console.log('System theme changed:', isDarkMode ? 'dark' : 'light');
        
        // Notify renderer process
        if (this.mainWindow) {
            this.mainWindow.webContents.send('theme-changed', isDarkMode);
        }
    }
    
    onSystemSleep() {
        console.log('System going to sleep');
        // Save application state
    }
    
    onSystemWake() {
        console.log('System waking up');
        // Restore application state
    }
    
    openExternal(url) {
        shell.openExternal(url);
    }
    
    showItemInFolder(path) {
        shell.showItemInFolder(path);
    }
    
    getSystemInfo() {
        return {
            platform: process.platform,
            arch: process.arch,
            version: process.version,
            osType: os.type(),
            osRelease: os.release(),
            totalMemory: os.totalmem(),
            freeMemory: os.freemem(),
            cpus: os.cpus().length
        };
    }
    
    async requestNotificationPermission() {
        if (process.platform === 'win32') {
            return true; // Windows doesn't require explicit permission
        }
        
        if (process.platform === 'darwin') {
            const permission = await systemPreferences.askForMediaAccess('microphone');
            return permission;
        }
        
        return true;
    }
}

module.exports = SystemIntegration;
\`\`\`
      `,
      learningObjectives: [
        'Set up Electron development environment',
        'Integrate React with Electron',
        'Implement secure IPC communication',
        'Add system integration features'
      ]
    },
    {
      id: 'wpf-development',
      title: 'WPF with C#',
      description: 'Create native Windows applications using WPF and C#.',
      duration: '3 hours',
      difficulty: 'intermediate',
      content: `
# WPF Development with C#

## WPF Fundamentals

### XAML Basics
\`\`\`xml
<!-- MainWindow.xaml -->
<Window x:Class="MyWPFApp.MainWindow"
        xmlns="http://schemas.microsoft.com/winfx/2006/xaml/presentation"
        xmlns:x="http://schemas.microsoft.com/winfx/2006/xaml"
        Title="My WPF Application" 
        Height="600" Width="800"
        MinHeight="400" MinWidth="600"
        WindowStartupLocation="CenterScreen">
    
    <Window.Resources>
        <!-- Define reusable styles -->
        <Style x:Key="HeaderTextStyle" TargetType="TextBlock">
            <Setter Property="FontSize" Value="18"/>
            <Setter Property="FontWeight" Value="Bold"/>
            <Setter Property="Foreground" Value="DarkBlue"/>
            <Setter Property="Margin" Value="10"/>
        </Style>
        
        <Style x:Key="ButtonStyle" TargetType="Button">
            <Setter Property="Padding" Value="10,5"/>
            <Setter Property="Margin" Value="5"/>
            <Setter Property="Background" Value="LightBlue"/>
            <Setter Property="BorderBrush" Value="DarkBlue"/>
            <Setter Property="BorderThickness" Value="1"/>
            <Style.Triggers>
                <Trigger Property="IsMouseOver" Value="True">
                    <Setter Property="Background" Value="SkyBlue"/>
                </Trigger>
            </Style.Triggers>
        </Style>
    </Window.Resources>
    
    <Grid>
        <Grid.RowDefinitions>
            <RowDefinition Height="Auto"/>
            <RowDefinition Height="*"/>
            <RowDefinition Height="Auto"/>
        </Grid.RowDefinitions>
        
        <!-- Header -->
        <StackPanel Grid.Row="0" Orientation="Horizontal" Background="LightGray">
            <TextBlock Text="WPF Application" Style="{StaticResource HeaderTextStyle}"/>
            <Button Content="Settings" Style="{StaticResource ButtonStyle}" Click="SettingsButton_Click"/>
        </StackPanel>
        
        <!-- Main Content -->
        <TabControl Grid.Row="1" Margin="10">
            <TabItem Header="Home">
                <ContentControl x:Name="HomeContent"/>
            </TabItem>
            <TabItem Header="Data">
                <ContentControl x:Name="DataContent"/>
            </TabItem>
            <TabItem Header="Settings">
                <ContentControl x:Name="SettingsContent"/>
            </TabItem>
        </TabControl>
        
        <!-- Status Bar -->
        <StatusBar Grid.Row="2">
            <StatusBarItem>
                <TextBlock x:Name="StatusText" Text="Ready"/>
            </StatusBarItem>
            <StatusBarItem HorizontalAlignment="Right">
                <TextBlock x:Name="TimeText"/>
            </StatusBarItem>
        </StatusBar>
    </Grid>
</Window>
\`\`\`

### Code-Behind and Event Handling
\`\`\`csharp
// MainWindow.xaml.cs
using System;
using System.Windows;
using System.Windows.Threading;

namespace MyWPFApp
{
    public partial class MainWindow : Window
    {
        private DispatcherTimer timer;
        
        public MainWindow()
        {
            InitializeComponent();
            InitializeApp();
        }
        
        private void InitializeApp()
        {
            // Setup timer for status bar
            timer = new DispatcherTimer();
            timer.Interval = TimeSpan.FromSeconds(1);
            timer.Tick += Timer_Tick;
            timer.Start();
            
            // Load content for tabs
            LoadTabContents();
            
            // Set initial status
            StatusText.Text = "Application loaded successfully";
        }
        
        private void Timer_Tick(object sender, EventArgs e)
        {
            TimeText.Text = DateTime.Now.ToString("HH:mm:ss");
        }
        
        private void LoadTabContents()
        {
            // Load home content
            HomeContent.Content = new HomeUserControl();
            
            // Load data content
            DataContent.Content = new DataUserControl();
            
            // Load settings content
            SettingsContent.Content = new SettingsUserControl();
        }
        
        private void SettingsButton_Click(object sender, RoutedEventArgs e)
        {
            var settingsWindow = new SettingsWindow();
            settingsWindow.Owner = this;
            settingsWindow.ShowDialog();
        }
        
        protected override void OnClosed(EventArgs e)
        {
            timer?.Stop();
            base.OnClosed(e);
        }
    }
}
\`\`\`

## MVVM Pattern Implementation

### ViewModel Base Class
\`\`\`csharp
// ViewModelBase.cs
using System;
using System.ComponentModel;
using System.Runtime.CompilerServices;

public abstract class ViewModelBase : INotifyPropertyChanged
{
    public event PropertyChangedEventHandler PropertyChanged;
    
    protected virtual void OnPropertyChanged([CallerMemberName] string propertyName = null)
    {
        PropertyChanged?.Invoke(this, new PropertyChangedEventArgs(propertyName));
    }
    
    protected virtual bool SetProperty<T>(ref T storage, T value, [CallerMemberName] string propertyName = null)
    {
        if (object.Equals(storage, value))
            return false;
        
        storage = value;
        OnPropertyChanged(propertyName);
        return true;
    }
}

// RelayCommand.cs
using System;
using System.Windows.Input;

public class RelayCommand : ICommand
{
    private readonly Action<object> _execute;
    private readonly Predicate<object> _canExecute;
    
    public RelayCommand(Action<object> execute, Predicate<object> canExecute = null)
    {
        _execute = execute ?? throw new ArgumentNullException(nameof(execute));
        _canExecute = canExecute;
    }
    
    public RelayCommand(Action execute, Func<bool> canExecute = null)
    {
        _execute = _ => execute();
        _canExecute = canExecute != null ? _ => canExecute() : null;
    }
    
    public event EventHandler CanExecuteChanged
    {
        add { CommandManager.RequerySuggested += value; }
        remove { CommandManager.RequerySuggested -= value; }
    }
    
    public bool CanExecute(object parameter)
    {
        return _canExecute?.Invoke(parameter) ?? true;
    }
    
    public void Execute(object parameter)
    {
        _execute(parameter);
    }
}
\`\`\`

### Main ViewModel
\`\`\`csharp
// MainViewModel.cs
using System;
using System.Collections.ObjectModel;
using System.Linq;
using System.Windows.Input;
using System.Threading.Tasks;

public class MainViewModel : ViewModelBase
{
    private string _statusText = "Ready";
    private bool _isLoading = false;
    private ObservableCollection<DataItem> _dataItems;
    private DataItem _selectedItem;
    
    public MainViewModel()
    {
        DataItems = new ObservableCollection<DataItem>();
        LoadDataCommand = new RelayCommand(async () => await LoadDataAsync(), () => !IsLoading);
        AddItemCommand = new RelayCommand(AddItem, () => !IsLoading);
        DeleteItemCommand = new RelayCommand(DeleteItem, () => SelectedItem != null && !IsLoading);
        SaveCommand = new RelayCommand(async () => await SaveAsync(), () => !IsLoading);
        
        // Load initial data
        _ = LoadDataAsync();
    }
    
    public string StatusText
    {
        get => _statusText;
        set => SetProperty(ref _statusText, value);
    }
    
    public bool IsLoading
    {
        get => _isLoading;
        set
        {
            if (SetProperty(ref _isLoading, value))
            {
                // Refresh command states when loading changes
                CommandManager.InvalidateRequerySuggested();
            }
        }
    }
    
    public ObservableCollection<DataItem> DataItems
    {
        get => _dataItems;
        set => SetProperty(ref _dataItems, value);
    }
    
    public DataItem SelectedItem
    {
        get => _selectedItem;
        set
        {
            if (SetProperty(ref _selectedItem, value))
            {
                CommandManager.InvalidateRequerySuggested();
            }
        }
    }
    
    public ICommand LoadDataCommand { get; }
    public ICommand AddItemCommand { get; }
    public ICommand DeleteItemCommand { get; }
    public ICommand SaveCommand { get; }
    
    private async Task LoadDataAsync()
    {
        try
        {
            IsLoading = true;
            StatusText = "Loading data...";
            
            // Simulate async data loading
            await Task.Delay(1000);
            
            var items = await DataService.GetDataItemsAsync();
            
            DataItems.Clear();
            foreach (var item in items)
            {
                DataItems.Add(item);
            }
            
            StatusText = $"Loaded {DataItems.Count} items";
        }
        catch (Exception ex)
        {
            StatusText = $"Error loading data: {ex.Message}";
        }
        finally
        {
            IsLoading = false;
        }
    }
    
    private void AddItem()
    {
        var newItem = new DataItem
        {
            Id = Guid.NewGuid(),
            Name = "New Item",
            Created = DateTime.Now,
            IsModified = true
        };
        
        DataItems.Add(newItem);
        SelectedItem = newItem;
        StatusText = "Item added";
    }
    
    private void DeleteItem()
    {
        if (SelectedItem != null)
        {
            DataItems.Remove(SelectedItem);
            StatusText = "Item deleted";
        }
    }
    
    private async Task SaveAsync()
    {
        try
        {
            IsLoading = true;
            StatusText = "Saving...";
            
            var modifiedItems = DataItems.Where(item => item.IsModified).ToList();
            
            foreach (var item in modifiedItems)
            {
                await DataService.SaveDataItemAsync(item);
                item.IsModified = false;
            }
            
            StatusText = $"Saved {modifiedItems.Count} items";
        }
        catch (Exception ex)
        {
            StatusText = $"Error saving: {ex.Message}";
        }
        finally
        {
            IsLoading = false;
        }
    }
}

// DataItem.cs
public class DataItem : ViewModelBase
{
    private string _name;
    private string _description;
    private bool _isModified;
    
    public Guid Id { get; set; }
    
    public string Name
    {
        get => _name;
        set
        {
            if (SetProperty(ref _name, value))
            {
                IsModified = true;
            }
        }
    }
    
    public string Description
    {
        get => _description;
        set
        {
            if (SetProperty(ref _description, value))
            {
                IsModified = true;
            }
        }
    }
    
    public DateTime Created { get; set; }
    
    public bool IsModified
    {
        get => _isModified;
        set => SetProperty(ref _isModified, value);
    }
}
\`\`\`

## Data Binding and Controls

### Advanced Data Binding
\`\`\`xml
<!-- DataUserControl.xaml -->
<UserControl x:Class="MyWPFApp.DataUserControl"
             xmlns="http://schemas.microsoft.com/winfx/2006/xaml/presentation"
             xmlns:x="http://schemas.microsoft.com/winfx/2006/xaml">
    
    <UserControl.Resources>
        <!-- Value converters -->
        <BooleanToVisibilityConverter x:Key="BoolToVisibilityConverter"/>
        
        <!-- Data templates -->
        <DataTemplate x:Key="DataItemTemplate">
            <Border BorderBrush="Gray" BorderThickness="1" Margin="2" Padding="5">
                <Grid>
                    <Grid.ColumnDefinitions>
                        <ColumnDefinition Width="*"/>
                        <ColumnDefinition Width="Auto"/>
                    </Grid.ColumnDefinitions>
                    
                    <StackPanel Grid.Column="0">
                        <TextBlock Text="{Binding Name}" FontWeight="Bold"/>
                        <TextBlock Text="{Binding Description}" FontStyle="Italic"/>
                        <TextBlock Text="{Binding Created, StringFormat='Created: {0:yyyy-MM-dd HH:mm}'}" 
                                   FontSize="10" Foreground="Gray"/>
                    </StackPanel>
                    
                    <Rectangle Grid.Column="1" Width="10" Height="10" Fill="Red" 
                               Visibility="{Binding IsModified, Converter={StaticResource BoolToVisibilityConverter}}"/>
                </Grid>
            </Border>
        </DataTemplate>
    </UserControl.Resources>
    
    <Grid>
        <Grid.RowDefinitions>
            <RowDefinition Height="Auto"/>
            <RowDefinition Height="*"/>
            <RowDefinition Height="Auto"/>
        </Grid.RowDefinitions>
        <Grid.ColumnDefinitions>
            <ColumnDefinition Width="300"/>
            <ColumnDefinition Width="*"/>
        </Grid.ColumnDefinitions>
        
        <!-- Toolbar -->
        <ToolBar Grid.Row="0" Grid.ColumnSpan="2">
            <Button Content="Load" Command="{Binding LoadDataCommand}"/>
            <Button Content="Add" Command="{Binding AddItemCommand}"/>
            <Button Content="Delete" Command="{Binding DeleteItemCommand}"/>
            <Separator/>
            <Button Content="Save" Command="{Binding SaveCommand}"/>
            <ProgressBar Width="100" Height="16" 
                         Visibility="{Binding IsLoading, Converter={StaticResource BoolToVisibilityConverter}}"
                         IsIndeterminate="True"/>
        </ToolBar>
        
        <!-- Item List -->
        <ListBox Grid.Row="1" Grid.Column="0" 
                 ItemsSource="{Binding DataItems}"
                 SelectedItem="{Binding SelectedItem}"
                 ItemTemplate="{StaticResource DataItemTemplate}"/>
        
        <!-- Detail View -->
        <GroupBox Grid.Row="1" Grid.Column="1" Header="Details" Margin="10">
            <Grid DataContext="{Binding SelectedItem}" IsEnabled="{Binding DataContext.SelectedItem, RelativeSource={RelativeSource AncestorType=UserControl}, Converter={StaticResource NotNullConverter}}">
                <Grid.RowDefinitions>
                    <RowDefinition Height="Auto"/>
                    <RowDefinition Height="Auto"/>
                    <RowDefinition Height="*"/>
                </Grid.RowDefinitions>
                
                <StackPanel Grid.Row="0" Margin="5">
                    <Label Content="Name:"/>
                    <TextBox Text="{Binding Name, UpdateSourceTrigger=PropertyChanged}"/>
                </StackPanel>
                
                <StackPanel Grid.Row="1" Margin="5">
                    <Label Content="Description:"/>
                    <TextBox Text="{Binding Description, UpdateSourceTrigger=PropertyChanged}" 
                             Height="60" VerticalScrollBarVisibility="Auto" 
                             TextWrapping="Wrap" AcceptsReturn="True"/>
                </StackPanel>
                
                <StackPanel Grid.Row="2" Margin="5" VerticalAlignment="Bottom">
                    <TextBlock Text="{Binding Created, StringFormat='Created: {0:F}'}"/>
                    <TextBlock Text="{Binding IsModified, StringFormat='Modified: {0}'}" Foreground="Red"/>
                </StackPanel>
            </Grid>
        </GroupBox>
        
        <!-- Status -->
        <TextBlock Grid.Row="2" Grid.ColumnSpan="2" 
                   Text="{Binding StatusText}" 
                   Margin="5" Padding="5"
                   Background="LightGray"/>
    </Grid>
</UserControl>
\`\`\`

## Custom Controls and Styling

### Custom User Control
\`\`\`csharp
// CustomButton.cs
using System.Windows;
using System.Windows.Controls;
using System.Windows.Media;

public class CustomButton : Button
{
    static CustomButton()
    {
        DefaultStyleKeyProperty.OverrideMetadata(typeof(CustomButton), 
            new FrameworkPropertyMetadata(typeof(CustomButton)));
    }
    
    public static readonly DependencyProperty CornerRadiusProperty =
        DependencyProperty.Register("CornerRadius", typeof(CornerRadius), typeof(CustomButton),
            new PropertyMetadata(new CornerRadius(0)));
    
    public static readonly DependencyProperty IconProperty =
        DependencyProperty.Register("Icon", typeof(Geometry), typeof(CustomButton),
            new PropertyMetadata(null));
    
    public CornerRadius CornerRadius
    {
        get { return (CornerRadius)GetValue(CornerRadiusProperty); }
        set { SetValue(CornerRadiusProperty, value); }
    }
    
    public Geometry Icon
    {
        get { return (Geometry)GetValue(IconProperty); }
        set { SetValue(IconProperty, value); }
    }
}
\`\`\`

### Custom Control Template
\`\`\`xml
<!-- Themes/Generic.xaml -->
<ResourceDictionary xmlns="http://schemas.microsoft.com/winfx/2006/xaml/presentation"
                    xmlns:x="http://schemas.microsoft.com/winfx/2006/xaml"
                    xmlns:local="clr-namespace:MyWPFApp">
    
    <Style TargetType="{x:Type local:CustomButton}">
        <Setter Property="Template">
            <Setter.Value>
                <ControlTemplate TargetType="{x:Type local:CustomButton}">
                    <Border x:Name="border" 
                            Background="{TemplateBinding Background}"
                            BorderBrush="{TemplateBinding BorderBrush}"
                            BorderThickness="{TemplateBinding BorderThickness}"
                            CornerRadius="{TemplateBinding CornerRadius}"
                            SnapsToDevicePixels="true">
                        <Grid>
                            <Grid.ColumnDefinitions>
                                <ColumnDefinition Width="Auto"/>
                                <ColumnDefinition Width="*"/>
                            </Grid.ColumnDefinitions>
                            
                            <Path x:Name="icon" 
                                  Grid.Column="0"
                                  Data="{TemplateBinding Icon}"
                                  Fill="{TemplateBinding Foreground}"
                                  Stretch="Uniform"
                                  Width="16" Height="16"
                                  Margin="5,0,5,0"
                                  VerticalAlignment="Center"
                                  Visibility="{Binding Icon, RelativeSource={RelativeSource TemplatedParent}, Converter={StaticResource NullToVisibilityConverter}}"/>
                            
                            <ContentPresenter x:Name="contentPresenter" 
                                              Grid.Column="1"
                                              Focusable="False" 
                                              HorizontalAlignment="{TemplateBinding HorizontalContentAlignment}" 
                                              Margin="{TemplateBinding Padding}" 
                                              RecognizesAccessKey="True" 
                                              SnapsToDevicePixels="{TemplateBinding SnapsToDevicePixels}" 
                                              VerticalAlignment="{TemplateBinding VerticalContentAlignment}"/>
                        </Grid>
                    </Border>
                    
                    <ControlTemplate.Triggers>
                        <Trigger Property="IsDefaulted" Value="true">
                            <Setter Property="BorderBrush" TargetName="border" Value="{DynamicResource {x:Static SystemColors.HighlightBrushKey}}"/>
                        </Trigger>
                        <Trigger Property="IsMouseOver" Value="true">
                            <Setter Property="Background" TargetName="border" Value="#FFBEE6FD"/>
                            <Setter Property="BorderBrush" TargetName="border" Value="#FF70C0E7"/>
                        </Trigger>
                        <Trigger Property="IsPressed" Value="true">
                            <Setter Property="Background" TargetName="border" Value="#FFC4E5F6"/>
                            <Setter Property="BorderBrush" TargetName="border" Value="#FF2C628B"/>
                        </Trigger>
                        <Trigger Property="IsEnabled" Value="false">
                            <Setter Property="Background" TargetName="border" Value="#FFF4F4F4"/>
                            <Setter Property="BorderBrush" TargetName="border" Value="#FFADB2B5"/>
                            <Setter Property="TextElement.Foreground" TargetName="contentPresenter" Value="#FF838383"/>
                            <Setter Property="Fill" TargetName="icon" Value="#FF838383"/>
                        </Trigger>
                    </ControlTemplate.Triggers>
                </ControlTemplate>
            </Setter.Value>
        </Setter>
    </Style>
</ResourceDictionary>
\`\`\`
      `,
      learningObjectives: [
        'Master XAML and WPF layout systems',
        'Implement MVVM pattern effectively',
        'Create advanced data binding scenarios',
        'Build custom controls and styling'
      ]
    },
    {
      id: 'javafx-development',
      title: 'JavaFX Desktop Applications',
      description: 'Develop rich desktop applications using JavaFX and Java.',
      duration: '3 hours',
      difficulty: 'intermediate',
      content: `
# JavaFX Desktop Applications

## JavaFX Fundamentals

### Project Setup and Structure
\`\`\`java
// Main.java - Application entry point
package com.example.javafxapp;

import javafx.application.Application;
import javafx.fxml.FXMLLoader;
import javafx.scene.Scene;
import javafx.scene.image.Image;
import javafx.stage.Stage;

public class Main extends Application {
    
    @Override
    public void start(Stage primaryStage) throws Exception {
        // Load FXML
        FXMLLoader loader = new FXMLLoader(getClass().getResource("/fxml/MainView.fxml"));
        Scene scene = new Scene(loader.load());
        
        // Set up the primary stage
        primaryStage.setTitle("JavaFX Desktop Application");
        primaryStage.setScene(scene);
        primaryStage.setMinWidth(800);
        primaryStage.setMinHeight(600);
        
        // Set application icon
        primaryStage.getIcons().add(new Image(getClass().getResourceAsStream("/icons/app-icon.png")));
        
        // Handle close request
        primaryStage.setOnCloseRequest(event -> {
            // Perform cleanup or show confirmation dialog
            handleApplicationExit();
        });
        
        primaryStage.show();
    }
    
    private void handleApplicationExit() {
        // Save application state, close resources, etc.
        System.out.println("Application is closing...");
        
        // Example: Save user preferences
        PreferencesManager.savePreferences();
        
        // Example: Close database connections
        DatabaseManager.closeConnections();
    }
    
    public static void main(String[] args) {
        // Set system properties before launching
        System.setProperty("javafx.preloader", "com.example.javafxapp.AppPreloader");
        
        launch(args);
    }
}
\`\`\`

### FXML and Scene Builder
\`\`\`xml
<!-- MainView.fxml -->
<?xml version="1.0" encoding="UTF-8"?>

<?import javafx.geometry.Insets?>
<?import javafx.scene.control.*?>
<?import javafx.scene.layout.*?>

<BorderPane xmlns="http://javafx.com/javafx/11.0.1" 
            xmlns:fx="http://javafx.com/fxml/1" 
            fx:controller="com.example.javafxapp.controller.MainController">
   
   <!-- Top Menu Bar -->
   <top>
      <VBox>
         <MenuBar>
            <Menu text="File">
               <MenuItem text="New" onAction="#handleNew" accelerator="Ctrl+N"/>
               <MenuItem text="Open" onAction="#handleOpen" accelerator="Ctrl+O"/>
               <MenuItem text="Save" onAction="#handleSave" accelerator="Ctrl+S"/>
               <SeparatorMenuItem />
               <MenuItem text="Exit" onAction="#handleExit"/>
            </Menu>
            <Menu text="Edit">
               <MenuItem text="Cut" onAction="#handleCut"/>
               <MenuItem text="Copy" onAction="#handleCopy"/>
               <MenuItem text="Paste" onAction="#handlePaste"/>
            </Menu>
            <Menu text="Help">
               <MenuItem text="About" onAction="#handleAbout"/>
            </Menu>
         </MenuBar>
         
         <!-- Toolbar -->
         <ToolBar>
            <Button text="New" onAction="#handleNew" fx:id="newButton">
               <graphic>
                  <ImageView fitHeight="16" fitWidth="16" preserveRatio="true">
                     <Image url="/icons/new.png"/>
                  </ImageView>
               </graphic>
            </Button>
            <Button text="Open" onAction="#handleOpen" fx:id="openButton"/>
            <Button text="Save" onAction="#handleSave" fx:id="saveButton"/>
            <Separator />
            <Button text="Settings" onAction="#handleSettings"/>
         </ToolBar>
      </VBox>
   </top>
   
   <!-- Center Content -->
   <center>
      <SplitPane dividerPositions="0.3" orientation="HORIZONTAL">
         
         <!-- Left Panel - Navigation -->
         <VBox spacing="5">
            <padding>
               <Insets bottom="10" left="10" right="10" top="10" />
            </padding>
            
            <Label text="Navigation" styleClass="section-header"/>
            <TreeView fx:id="navigationTree" prefHeight="200"/>
            
            <Label text="Properties" styleClass="section-header"/>
            <TableView fx:id="propertiesTable" prefHeight="150">
               <columns>
                  <TableColumn fx:id="propertyNameColumn" text="Property"/>
                  <TableColumn fx:id="propertyValueColumn" text="Value"/>
               </columns>
            </TableView>
         </VBox>
         
         <!-- Right Panel - Main Content -->
         <VBox spacing="10">
            <padding>
               <Insets bottom="10" left="10" right="10" top="10" />
            </padding>
            
            <TabPane fx:id="contentTabPane" tabClosingPolicy="SELECTED_TAB">
               <Tab text="Home" closable="false">
                  <content>
                     <fx:include source="HomeView.fxml"/>
                  </content>
               </Tab>
               <Tab text="Data" closable="false">
                  <content>
                     <fx:include source="DataView.fxml"/>
                  </content>
               </Tab>
            </TabPane>
         </VBox>
         
      </SplitPane>
   </center>
   
   <!-- Bottom Status Bar -->
   <bottom>
      <HBox alignment="CENTER_LEFT" spacing="10" styleClass="status-bar">
         <padding>
            <Insets bottom="5" left="10" right="10" top="5" />
         </padding>
         
         <Label fx:id="statusLabel" text="Ready"/>
         <Region HBox.hgrow="ALWAYS"/>
         <ProgressIndicator fx:id="progressIndicator" visible="false" prefHeight="16" prefWidth="16"/>
         <Label fx:id="timeLabel"/>
      </HBox>
   </bottom>
   
</BorderPane>
\`\`\`

## Controller Pattern and Event Handling

### Main Controller
\`\`\`java
// MainController.java
package com.example.javafxapp.controller;

import javafx.animation.Timeline;
import javafx.animation.KeyFrame;
import javafx.application.Platform;
import javafx.concurrent.Task;
import javafx.fxml.FXML;
import javafx.fxml.Initializable;
import javafx.scene.control.*;
import javafx.scene.control.cell.PropertyValueFactory;
import javafx.stage.FileChooser;
import javafx.util.Duration;

import java.io.File;
import java.net.URL;
import java.time.LocalTime;
import java.time.format.DateTimeFormatter;
import java.util.ResourceBundle;

public class MainController implements Initializable {
    
    @FXML private TreeView<String> navigationTree;
    @FXML private TableView<PropertyItem> propertiesTable;
    @FXML private TableColumn<PropertyItem, String> propertyNameColumn;
    @FXML private TableColumn<PropertyItem, String> propertyValueColumn;
    @FXML private TabPane contentTabPane;
    @FXML private Label statusLabel;
    @FXML private Label timeLabel;
    @FXML private ProgressIndicator progressIndicator;
    @FXML private Button newButton;
    @FXML private Button openButton;
    @FXML private Button saveButton;
    
    private Timeline clockTimeline;
    private DataService dataService;
    
    @Override
    public void initialize(URL location, ResourceBundle resources) {
        setupNavigationTree();
        setupPropertiesTable();
        setupClock();
        
        dataService = new DataService();
        
        // Initialize UI state
        updateUIState();
    }
    
    private void setupNavigationTree() {
        TreeItem<String> rootItem = new TreeItem<>("Root");
        rootItem.setExpanded(true);
        
        TreeItem<String> projectsItem = new TreeItem<>("Projects");
        TreeItem<String> documentsItem = new TreeItem<>("Documents");
        TreeItem<String> settingsItem = new TreeItem<>("Settings");
        
        rootItem.getChildren().addAll(projectsItem, documentsItem, settingsItem);
        
        navigationTree.setRoot(rootItem);
        navigationTree.getSelectionModel().selectedItemProperty().addListener(
            (observable, oldValue, newValue) -> handleNavigationSelection(newValue)
        );
    }
    
    private void setupPropertiesTable() {
        propertyNameColumn.setCellValueFactory(new PropertyValueFactory<>("name"));
        propertyValueColumn.setCellValueFactory(new PropertyValueFactory<>("value"));
        
        // Make value column editable
        propertyValueColumn.setCellFactory(TextFieldTableCell.forTableColumn());
        propertyValueColumn.setOnEditCommit(event -> {
            PropertyItem item = event.getRowValue();
            item.setValue(event.getNewValue());
            handlePropertyChanged(item);
        });
        
        propertiesTable.setEditable(true);
    }
    
    private void setupClock() {
        clockTimeline = new Timeline(new KeyFrame(Duration.seconds(1), event -> {
            timeLabel.setText(LocalTime.now().format(DateTimeFormatter.ofPattern("HH:mm:ss")));
        }));
        clockTimeline.setCycleCount(Timeline.INDEFINITE);
        clockTimeline.play();
    }
    
    private void handleNavigationSelection(TreeItem<String> selectedItem) {
        if (selectedItem != null) {
            statusLabel.setText("Selected: " + selectedItem.getValue());
            loadPropertiesForItem(selectedItem.getValue());
        }
    }
    
    private void loadPropertiesForItem(String itemName) {
        // Load properties based on selected item
        propertiesTable.getItems().clear();
        
        switch (itemName) {
            case "Projects":
                propertiesTable.getItems().addAll(
                    new PropertyItem("Count", "5"),
                    new PropertyItem("Last Modified", "2023-12-01"),
                    new PropertyItem("Status", "Active")
                );
                break;
            case "Documents":
                propertiesTable.getItems().addAll(
                    new PropertyItem("Total Size", "45.2 MB"),
                    new PropertyItem("File Count", "127"),
                    new PropertyItem("Type", "Mixed")
                );
                break;
            default:
                propertiesTable.getItems().add(new PropertyItem("Name", itemName));
                break;
        }
    }
    
    private void handlePropertyChanged(PropertyItem item) {
        statusLabel.setText("Property '" + item.getName() + "' changed to: " + item.getValue());
    }
    
    @FXML
    private void handleNew() {
        statusLabel.setText("Creating new file...");
        
        // Simulate async operation
        Task<Void> task = new Task<Void>() {
            @Override
            protected Void call() throws Exception {
                Thread.sleep(1000); // Simulate work
                return null;
            }
            
            @Override
            protected void succeeded() {
                Platform.runLater(() -> {
                    statusLabel.setText("New file created");
                    progressIndicator.setVisible(false);
                });
            }
        };
        
        progressIndicator.setVisible(true);
        new Thread(task).start();
    }
    
    @FXML
    private void handleOpen() {
        FileChooser fileChooser = new FileChooser();
        fileChooser.setTitle("Open File");
        fileChooser.getExtensionFilters().addAll(
            new FileChooser.ExtensionFilter("Text Files", "*.txt"),
            new FileChooser.ExtensionFilter("All Files", "*.*")
        );
        
        File selectedFile = fileChooser.showOpenDialog(navigationTree.getScene().getWindow());
        
        if (selectedFile != null) {
            statusLabel.setText("Opening: " + selectedFile.getName());
            loadFile(selectedFile);
        }
    }
    
    @FXML
    private void handleSave() {
        statusLabel.setText("Saving file...");
        
        Task<Void> saveTask = new Task<Void>() {
            @Override
            protected Void call() throws Exception {
                // Simulate save operation
                dataService.saveData();
                Thread.sleep(500);
                return null;
            }
            
            @Override
            protected void succeeded() {
                Platform.runLater(() -> {
                    statusLabel.setText("File saved successfully");
                    progressIndicator.setVisible(false);
                });
            }
            
            @Override
            protected void failed() {
                Platform.runLater(() -> {
                    statusLabel.setText("Save failed: " + getException().getMessage());
                    progressIndicator.setVisible(false);
                    showErrorDialog("Save Error", "Failed to save file", getException());
                });
            }
        };
        
        progressIndicator.setVisible(true);
        new Thread(saveTask).start();
    }
    
    @FXML
    private void handleSettings() {
        try {
            SettingsDialog dialog = new SettingsDialog();
            dialog.showAndWait();
        } catch (Exception e) {
            showErrorDialog("Error", "Failed to open settings", e);
        }
    }
    
    @FXML
    private void handleAbout() {
        Alert alert = new Alert(Alert.AlertType.INFORMATION);
        alert.setTitle("About");
        alert.setHeaderText("JavaFX Desktop Application");
        alert.setContentText("Version 1.0.0\\nBuilt with JavaFX and Java 11");
        alert.showAndWait();
    }
    
    @FXML
    private void handleExit() {
        // Show confirmation dialog
        Alert alert = new Alert(Alert.AlertType.CONFIRMATION);
        alert.setTitle("Exit Application");
        alert.setHeaderText("Are you sure you want to exit?");
        alert.setContentText("Any unsaved changes will be lost.");
        
        alert.showAndWait().ifPresent(response -> {
            if (response == ButtonType.OK) {
                Platform.exit();
            }
        });
    }
    
    private void loadFile(File file) {
        Task<String> loadTask = new Task<String>() {
            @Override
            protected String call() throws Exception {
                return dataService.loadFile(file);
            }
            
            @Override
            protected void succeeded() {
                Platform.runLater(() -> {
                    // Update UI with loaded content
                    statusLabel.setText("File loaded: " + file.getName());
                    progressIndicator.setVisible(false);
                });
            }
        };
        
        progressIndicator.setVisible(true);
        new Thread(loadTask).start();
    }
    
    private void updateUIState() {
        // Update button states based on application state
        boolean hasData = dataService.hasData();
        saveButton.setDisable(!hasData);
    }
    
    private void showErrorDialog(String title, String message, Throwable exception) {
        Alert alert = new Alert(Alert.AlertType.ERROR);
        alert.setTitle(title);
        alert.setHeaderText(message);
        alert.setContentText(exception != null ? exception.getMessage() : "Unknown error");
        alert.showAndWait();
    }
}

// PropertyItem.java
public class PropertyItem {
    private String name;
    private String value;
    
    public PropertyItem(String name, String value) {
        this.name = name;
        this.value = value;
    }
    
    // Getters and setters
    public String getName() { return name; }
    public void setName(String name) { this.name = name; }
    
    public String getValue() { return value; }
    public void setValue(String value) { this.value = value; }
}
\`\`\`

## Advanced UI Components

### Custom Controls and Styling
\`\`\`java
// CustomProgressIndicator.java
package com.example.javafxapp.controls;

import javafx.animation.RotateTransition;
import javafx.beans.property.DoubleProperty;
import javafx.beans.property.SimpleDoubleProperty;
import javafx.scene.control.Control;
import javafx.scene.control.Skin;
import javafx.util.Duration;

public class CustomProgressIndicator extends Control {
    
    private static final String DEFAULT_STYLE_CLASS = "custom-progress-indicator";
    
    private final DoubleProperty progress = new SimpleDoubleProperty(this, "progress", 0.0);
    
    public CustomProgressIndicator() {
        getStyleClass().add(DEFAULT_STYLE_CLASS);
    }
    
    @Override
    protected Skin<?> createDefaultSkin() {
        return new CustomProgressIndicatorSkin(this);
    }
    
    public final void setProgress(double value) {
        progress.set(value);
    }
    
    public final double getProgress() {
        return progress.get();
    }
    
    public final DoubleProperty progressProperty() {
        return progress;
    }
    
    // Inner skin class
    private static class CustomProgressIndicatorSkin implements Skin<CustomProgressIndicator> {
        private CustomProgressIndicator control;
        private StackPane container;
        private Circle progressCircle;
        private RotateTransition rotation;
        
        public CustomProgressIndicatorSkin(CustomProgressIndicator control) {
            this.control = control;
            createUI();
            setupBindings();
        }
        
        private void createUI() {
            container = new StackPane();
            container.setMaxSize(50, 50);
            container.setPrefSize(50, 50);
            
            progressCircle = new Circle(20);
            progressCircle.setFill(Color.TRANSPARENT);
            progressCircle.setStroke(Color.BLUE);
            progressCircle.setStrokeWidth(3);
            progressCircle.getStrokeDashArray().addAll(10d, 5d);
            
            container.getChildren().add(progressCircle);
            
            // Create rotation animation
            rotation = new RotateTransition(Duration.seconds(2), progressCircle);
            rotation.setByAngle(360);
            rotation.setCycleCount(RotateTransition.INDEFINITE);
            rotation.setAutoReverse(false);
        }
        
        private void setupBindings() {
            control.progressProperty().addListener((obs, oldVal, newVal) -> {
                if (newVal.doubleValue() > 0 && newVal.doubleValue() < 1) {
                    if (!rotation.getStatus().equals(Animation.Status.RUNNING)) {
                        rotation.play();
                    }
                } else {
                    rotation.stop();
                }
            });
        }
        
        @Override
        public CustomProgressIndicator getSkinnable() {
            return control;
        }
        
        @Override
        public Node getNode() {
            return container;
        }
        
        @Override
        public void dispose() {
            if (rotation != null) {
                rotation.stop();
            }
        }
    }
}
\`\`\`

### Data Binding and Observable Properties
\`\`\`java
// DataModel.java
package com.example.javafxapp.model;

import javafx.beans.property.*;
import javafx.collections.FXCollections;
import javafx.collections.ObservableList;

public class DataModel {
    
    private final StringProperty title = new SimpleStringProperty(this, "title", "");
    private final StringProperty description = new SimpleStringProperty(this, "description", "");
    private final BooleanProperty modified = new SimpleBooleanProperty(this, "modified", false);
    private final IntegerProperty itemCount = new SimpleIntegerProperty(this, "itemCount", 0);
    private final ObservableList<DataItem> items = FXCollections.observableArrayList();
    
    public DataModel() {
        // Set up property listeners
        title.addListener((obs, oldVal, newVal) -> setModified(true));
        description.addListener((obs, oldVal, newVal) -> setModified(true));
        
        // Update item count when items change
        items.addListener((javafx.collections.ListChangeListener<DataItem>) change -> {
            itemCount.set(items.size());
            setModified(true);
        });
    }
    
    // Title property
    public String getTitle() { return title.get(); }
    public void setTitle(String title) { this.title.set(title); }
    public StringProperty titleProperty() { return title; }
    
    // Description property
    public String getDescription() { return description.get(); }
    public void setDescription(String description) { this.description.set(description); }
    public StringProperty descriptionProperty() { return description; }
    
    // Modified property
    public boolean isModified() { return modified.get(); }
    public void setModified(boolean modified) { this.modified.set(modified); }
    public BooleanProperty modifiedProperty() { return modified; }
    
    // Item count property (read-only)
    public int getItemCount() { return itemCount.get(); }
    public ReadOnlyIntegerProperty itemCountProperty() { return itemCount; }
    
    // Items list
    public ObservableList<DataItem> getItems() { return items; }
    
    // Business methods
    public void addItem(DataItem item) {
        items.add(item);
    }
    
    public void removeItem(DataItem item) {
        items.remove(item);
    }
    
    public void clear() {
        items.clear();
        setTitle("");
        setDescription("");
        setModified(false);
    }
    
    public void save() {
        // Simulate save operation
        setModified(false);
    }
}

// DataItem.java
public class DataItem {
    private final StringProperty name = new SimpleStringProperty(this, "name", "");
    private final StringProperty value = new SimpleStringProperty(this, "value", "");
    private final ObjectProperty<LocalDateTime> timestamp = new SimpleObjectProperty<>(this, "timestamp", LocalDateTime.now());
    
    public DataItem() {}
    
    public DataItem(String name, String value) {
        setName(name);
        setValue(value);
    }
    
    // Name property
    public String getName() { return name.get(); }
    public void setName(String name) { this.name.set(name); }
    public StringProperty nameProperty() { return name; }
    
    // Value property
    public String getValue() { return value.get(); }
    public void setValue(String value) { this.value.set(value); }
    public StringProperty valueProperty() { return value; }
    
    // Timestamp property
    public LocalDateTime getTimestamp() { return timestamp.get(); }
    public void setTimestamp(LocalDateTime timestamp) { this.timestamp.set(timestamp); }
    public ObjectProperty<LocalDateTime> timestampProperty() { return timestamp; }
}
\`\`\`

## Packaging and Distribution

### Build Configuration (Maven)
\`\`\`xml
<!-- pom.xml -->
<?xml version="1.0" encoding="UTF-8"?>
<project xmlns="http://maven.apache.org/POM/4.0.0"
         xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
         xsi:schemaLocation="http://maven.apache.org/POM/4.0.0 
         http://maven.apache.org/xsd/maven-4.0.0.xsd">
    <modelVersion>4.0.0</modelVersion>
    
    <groupId>com.example</groupId>
    <artifactId>javafx-desktop-app</artifactId>
    <version>1.0.0</version>
    <packaging>jar</packaging>
    
    <properties>
        <maven.compiler.source>11</maven.compiler.source>
        <maven.compiler.target>11</maven.compiler.target>
        <javafx.version>17.0.2</javafx.version>
        <javafx.plugin.version>0.0.8</javafx.plugin.version>
    </properties>
    
    <dependencies>
        <dependency>
            <groupId>org.openjfx</groupId>
            <artifactId>javafx-controls</artifactId>
            <version>\${javafx.version}</version>
        </dependency>
        <dependency>
            <groupId>org.openjfx</groupId>
            <artifactId>javafx-fxml</artifactId>
            <version>\${javafx.version}</version>
        </dependency>
    </dependencies>
    
    <build>
        <plugins>
            <!-- JavaFX Maven Plugin -->
            <plugin>
                <groupId>org.openjfx</groupId>
                <artifactId>javafx-maven-plugin</artifactId>
                <version>\${javafx.plugin.version}</version>
                <configuration>
                    <mainClass>com.example.javafxapp.Main</mainClass>
                </configuration>
            </plugin>
            
            <!-- JLink Plugin for creating custom runtime image -->
            <plugin>
                <groupId>org.apache.maven.plugins</groupId>
                <artifactId>maven-jlink-plugin</artifactId>
                <version>3.1.0</version>
                <configuration>
                    <launcher>javafx-app=com.example.javafxapp/com.example.javafxapp.Main</launcher>
                    <jlinkImageName>javafx-app</jlinkImageName>
                    <noManPages>true</noManPages>
                    <noHeaderFiles>true</noHeaderFiles>
                </configuration>
            </plugin>
        </plugins>
    </build>
</project>
\`\`\`
      `,
      learningObjectives: [
        'Master JavaFX UI development with FXML',
        'Implement MVC pattern in JavaFX',
        'Create custom controls and styling',
        'Package JavaFX applications for distribution'
      ]
    }
  ]
};